import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '@/App';
import usePeriodChange from '@/hooks/usePeriodChange';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { pillTabsList, pillTabsTrigger } from '@/lib/tabStyles';
import { Building2, Search, Check, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@/components/ui/command';
import Purchases from './Purchases';
import Expenses from './Expenses';
import Sales from './Sales';
import Rekapan from './Rekapan';
import RekapanSemuaCabang from './RekapanSemuaCabang';
import RekapanSemuaPartai from './RekapanSemuaPartai';
import CentralExpenses from './CentralExpenses';
import BranchHistory from './BranchHistory';
import { exportBranchTransactionsPDF, exportAllBranchesTransactionsPDF } from '@/utils/exportBranchPDF';

const Transactions = ({ user }) => {
  const location = useLocation();
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [loading, setLoading] = useState(true);
  const [purchases, setPurchases] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [sales, setSales] = useState([]);
  const [open, setOpen] = useState(false);

  // Check if there's a selectedBranch from navigation state
  useEffect(() => {
    if (location.state?.selectedBranch) {
      setSelectedBranch(location.state.selectedBranch);
      // Clear the state after using it to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    // Fetch transactions when branch is selected or when it's "Semua Cabang" (empty string)
    if (selectedBranch !== null && selectedBranch !== undefined) {
      fetchTransactions();
    }
  }, [selectedBranch]);

  usePeriodChange(() => {
    if (selectedBranch !== null && selectedBranch !== undefined) {
      fetchTransactions();
    }
  });

  const fetchBranches = async () => {
    try {
      const response = await api.get('/branches');
      setBranches(response.data);
      
      // Set "Semua Cabang" (empty string) as default
      setSelectedBranch('');
      localStorage.setItem('selectedBranchId', '');
    } catch (error) {
      toast.error('Gagal memuat data cabang');
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      // Get selected period from localStorage
      const periodId = localStorage.getItem('selectedPeriodId');
      
      // Build query params
      const params = periodId ? { period_id: periodId } : {};
      
      const [purchasesRes, expensesRes, salesRes] = await Promise.all([
        api.get('/purchases', { params }),
        api.get('/expenses', { params }),
        api.get('/sales', { params })
      ]);
      
      // Filter by selected branch
      setPurchases(purchasesRes.data.filter(p => p.branch_id === selectedBranch));
      setExpenses(expensesRes.data.filter(e => e.branch_id === selectedBranch));
      setSales(salesRes.data.filter(s => s.branch_id === selectedBranch));
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    }
  };

  const [exporting, setExporting] = useState(false);

  const handleExportPDF = async () => {
    if (!selectedBranch || selectedBranch === '' || selectedBranch === 'PUSAT' || selectedBranch === 'ALL_PARTAI') {
      toast.error('Pilih cabang spesifik terlebih dahulu');
      return;
    }
    const branch = branches.find((b) => b.id === selectedBranch);
    if (!branch) {
      toast.error('Cabang tidak ditemukan');
      return;
    }
    setExporting(true);
    try {
      const periodId = localStorage.getItem('selectedPeriodId');
      let periodName = '';
      try {
        const periods = await api.get('/periods');
        periodName = periods.data.find((p) => p.id === periodId)?.name || '';
      } catch {/* ignore */}
      await exportBranchTransactionsPDF({
        branchId: selectedBranch,
        branchName: branch.name,
        periodId,
        periodName,
      });
      toast.success('PDF berhasil diunduh');
    } catch (error) {
      console.error('Export PDF error:', error);
      toast.error('Gagal mengekspor PDF');
    } finally {
      setExporting(false);
    }
  };

  const [bulkExporting, setBulkExporting] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0, branchName: '' });

  const handleExportAllPDF = async () => {
    const periodId = localStorage.getItem('selectedPeriodId');
    if (!periodId) {
      toast.error('Pilih periode terlebih dahulu');
      return;
    }
    setBulkExporting(true);
    setBulkProgress({ current: 0, total: 0, branchName: '' });
    try {
      let periodName = '';
      try {
        const periods = await api.get('/periods');
        periodName = periods.data.find((p) => p.id === periodId)?.name || '';
      } catch {/* ignore */}
      await exportAllBranchesTransactionsPDF({
        periodId,
        periodName,
        onProgress: (current, total, branchName) => {
          setBulkProgress({ current, total, branchName });
        },
      });
      toast.success('ZIP semua cabang berhasil diunduh');
    } catch (error) {
      console.error('Export all PDF error:', error);
      toast.error(error.message || 'Gagal mengekspor PDF semua cabang');
    } finally {
      setBulkExporting(false);
      setBulkProgress({ current: 0, total: 0, branchName: '' });
    }
  };

  const handleBranchChange = (branchId) => {
    setSelectedBranch(branchId);
    localStorage.setItem('selectedBranchId', branchId);
    
    let branchName;
    if (branchId === 'ALL_PARTAI') {
      branchName = 'Semua Partai';
    } else if (branchId === 'PUSAT') {
      branchName = 'Pusat';
    } else if (branchId === '') {
      branchName = 'Semua Cabang';
    } else {
      branchName = branches.find(b => b.id === branchId)?.name;
    }
    
    toast.success(`Dipilih: ${branchName}`);
  };

  const selectedBranchData = branches.find(b => b.id === selectedBranch);

  // Helper function to sort branches by status: ACTIVE → INACTIVE → BLACKLIST
  const getSortedBranches = (branchesList) => {
    const statusOrder = { 'ACTIVE': 1, 'INACTIVE': 2, 'BLACKLIST': 3 };
    return [...branchesList].sort((a, b) => {
      const aStatus = a.status || 'ACTIVE';
      const bStatus = b.status || 'ACTIVE';
      const aOrder = statusOrder[aStatus] || 4;
      const bOrder = statusOrder[bStatus] || 4;
      
      // Primary sort: by status
      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }
      
      // Secondary sort: by order_number within same status group
      const aOrderNum = a.order_number || 9999;
      const bOrderNum = b.order_number || 9999;
      
      if (aOrderNum !== bOrderNum) {
        return aOrderNum - bOrderNum;
      }
      
      // Tertiary sort: by name alphabetically
      return (a.name || '').localeCompare(b.name || '');
    });
  };

  // Calculate profit/loss
  const totalPurchases = purchases.reduce((sum, p) => sum + (p.total_amount || 0), 0);
  const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  const totalSales = sales.reduce((sum, s) => sum + (s.total_amount || 0), 0);
  const profitLoss = totalSales - totalExpenses - totalPurchases;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount).replace('Rp', 'Rp.');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  if (branches.length === 0) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk' }}>Transaksi</h1>
          <p className="text-slate-600 mt-1">Kelola pembelian, biaya, dan penjualan per cabang</p>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-600">Belum ada cabang. Silakan buat cabang terlebih dahulu.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="transactions-page">
      {/* Header with Branch Selector */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk' }}>Transaksi</h1>
          <p className="text-slate-600 mt-1">Kelola pembelian, biaya, dan penjualan</p>
        </div>
        <div className="flex items-center gap-3">
          {selectedBranch && selectedBranch !== '' && selectedBranch !== 'ALL_PARTAI' && selectedBranch !== 'PUSAT' && (
            <Button
              variant="outline"
              className="border-blue-600 text-blue-700 hover:bg-blue-50"
              onClick={handleExportPDF}
              disabled={exporting}
              data-testid="export-branch-pdf-btn"
            >
              <FileDown size={18} className="mr-2" />
              {exporting ? 'Mengekspor...' : 'Export PDF'}
            </Button>
          )}
          {(selectedBranch === '' || selectedBranch == null) && (
            <Button
              variant="outline"
              className="border-blue-600 text-blue-700 hover:bg-blue-50"
              onClick={handleExportAllPDF}
              disabled={bulkExporting}
              data-testid="export-all-pdf-btn"
            >
              <FileDown size={18} className="mr-2" />
              {bulkExporting
                ? `Memproses ${bulkProgress.current}/${bulkProgress.total}...`
                : 'Export PDF Semua Cabang'}
            </Button>
          )}
          <div className="flex items-center gap-2">
            <Building2 size={20} className="text-slate-600" />
            <span className="text-sm font-medium text-slate-600">Pilih Cabang:</span>
          </div>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className="w-64 justify-between"
                data-testid="branch-selector"
              >
                {selectedBranch === 'ALL_PARTAI'
                  ? "Semua Partai"
                  : selectedBranch === 'PUSAT'
                  ? "Pusat"
                  : selectedBranch
                  ? branches.find((branch) => branch.id === selectedBranch)?.name
                  : "Semua Cabang"}
                <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-0">
              <Command>
                <CommandInput placeholder="Cari cabang..." autoComplete="off" />
                <CommandEmpty>Cabang tidak ditemukan.</CommandEmpty>
                <CommandGroup>
                  {/* Option for "Semua Cabang" */}
                  <CommandItem
                    value="semua-cabang"
                    onSelect={() => {
                      handleBranchChange('');
                      setOpen(false);
                    }}
                    data-testid="branch-option-all"
                  >
                    <Check
                      className={`mr-2 h-4 w-4 ${
                        selectedBranch === '' ? "opacity-100" : "opacity-0"
                      }`}
                    />
                    Semua Cabang
                  </CommandItem>
                  {/* Option for "Pusat" */}
                  <CommandItem
                    value="pusat"
                    onSelect={() => {
                      handleBranchChange('PUSAT');
                      setOpen(false);
                    }}
                    data-testid="branch-option-pusat"
                  >
                    <Check
                      className={`mr-2 h-4 w-4 ${
                        selectedBranch === 'PUSAT' ? "opacity-100" : "opacity-0"
                      }`}
                    />
                    Pusat
                  </CommandItem>
                  {getSortedBranches(branches).map((branch) => (
                    <CommandItem
                      key={branch.id}
                      value={branch.name}
                      onSelect={() => {
                        handleBranchChange(branch.id);
                        setOpen(false);
                      }}
                      data-testid={`branch-option-${branch.id}`}
                    >
                      <Check
                        className={`mr-2 h-4 w-4 ${
                          selectedBranch === branch.id ? "opacity-100" : "opacity-0"
                        }`}
                      />
                      {branch.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Profit/Loss Card - Only show for specific branch (not for ALL_PARTAI, PUSAT, or Semua Cabang) */}
      {selectedBranch && selectedBranch !== '' && selectedBranch !== 'ALL_PARTAI' && selectedBranch !== 'PUSAT' && (
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-slate-600">Total Pembelian</p>
              <p className="text-xl font-bold text-orange-600">{formatCurrency(totalPurchases)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-slate-600">Total Biaya</p>
              <p className="text-xl font-bold text-red-600">{formatCurrency(totalExpenses)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-slate-600">Total Penjualan</p>
              <p className="text-xl font-bold text-green-600">{formatCurrency(totalSales)}</p>
            </CardContent>
          </Card>
          <Card className={profitLoss >= 0 ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-red-500'}>
            <CardContent className="pt-6">
              <p className="text-sm text-slate-600">Laba / Rugi</p>
              <p className={`text-2xl font-bold ${profitLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(profitLoss)}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Bulk Export Progress */}
      {bulkExporting && (
        <Card className="border-blue-300 bg-blue-50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-blue-900">
                Mengekspor PDF Semua Cabang
                {bulkProgress.branchName && ` — ${bulkProgress.branchName}`}
              </p>
              <p className="text-sm font-bold text-blue-900">
                {bulkProgress.current}/{bulkProgress.total || '—'}
              </p>
            </div>
            <div className="w-full h-3 bg-blue-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-600 transition-all duration-300"
                style={{
                  width: bulkProgress.total > 0
                    ? `${Math.min(100, (bulkProgress.current / bulkProgress.total) * 100)}%`
                    : '0%',
                }}
                data-testid="bulk-export-progress-bar"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Conditional rendering based on selected branch */}
      {selectedBranch === 'PUSAT' ? (
        /* Show Central Expenses when PUSAT selected */
        <CentralExpenses user={user} onDataChange={fetchTransactions} />
      ) : selectedBranch === 'ALL_PARTAI' ? (
        /* Show Rekapan Semua Partai when ALL_PARTAI selected */
        <RekapanSemuaPartai onBranchSelect={handleBranchChange} />
      ) : selectedBranch && selectedBranch !== '' ? (
        /* Tabs for Purchases, Expenses, Sales - Only show if specific branch selected */
        <div>
          <Tabs defaultValue="purchases" className="w-full">
            <TabsList className={pillTabsList}>
              <TabsTrigger value="purchases" className={pillTabsTrigger} data-testid="tab-purchases">Pembelian</TabsTrigger>
              <TabsTrigger value="expenses" className={pillTabsTrigger} data-testid="tab-expenses">Biaya</TabsTrigger>
              <TabsTrigger value="sales" className={pillTabsTrigger} data-testid="tab-sales">Penjualan</TabsTrigger>
              <TabsTrigger value="rekapan" className={pillTabsTrigger} data-testid="tab-rekapan">Rekapan</TabsTrigger>
              <TabsTrigger value="history" className={pillTabsTrigger} data-testid="tab-history">History</TabsTrigger>
            </TabsList>

            <TabsContent value="purchases" className="mt-6">
              <Purchases user={user} branchId={selectedBranch} onDataChange={fetchTransactions} />
            </TabsContent>

            <TabsContent value="expenses" className="mt-6">
              <Expenses user={user} branchId={selectedBranch} onDataChange={fetchTransactions} />
            </TabsContent>

            <TabsContent value="sales" className="mt-6">
              <Sales user={user} branchId={selectedBranch} onDataChange={fetchTransactions} />
            </TabsContent>

            <TabsContent value="rekapan" className="mt-6">
              <Rekapan branchId={selectedBranch} />
            </TabsContent>

            <TabsContent value="history" className="mt-6">
              <BranchHistory branchId={selectedBranch} />
            </TabsContent>
          </Tabs>
        </div>
      ) : (
        /* Show Rekapan Semua Cabang when no specific branch selected (empty string) */
        <RekapanSemuaCabang user={user} onBranchSelect={handleBranchChange} />
      )}
    </div>
  );
};

export default Transactions;
